import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import {
  VARIANT_MAP,
  staggerContainer,
  type VariantName,
} from '@/lib/animations';

interface Props {
  children: ReactNode;
  /** Variant à appliquer aux enfants. Default: fadeUp. */
  variant?: VariantName;
  /** Si défini, applique un stagger entre les enfants directs. */
  stagger?: number;
  /** Délai avant le déclenchement (en secondes). */
  delay?: number;
  /** Pourcentage du viewport requis pour déclencher l'animation. */
  amount?: number;
  /** Classe CSS appliquée à la section wrapper. */
  className?: string;
  /** Tag HTML rendu. Default: 'section'. */
  as?: 'section' | 'div' | 'article' | 'aside';
}

/**
 * Wrapper qui anime ses enfants quand ils entrent dans le viewport.
 *
 * Si `stagger` est défini : applique un effet stagger sur les enfants
 * directs (chaque enfant doit être une motion.div ou utiliser la prop
 * variants={VARIANT_MAP[variant]}).
 *
 * Sinon : applique simplement le variant à toute la section comme un bloc.
 */
export default function AnimatedSection({
  children,
  variant = 'fadeUp',
  stagger,
  delay = 0,
  amount = 0.15,
  className,
  as = 'section',
}: Props) {
  const MotionTag = motion[as];

  if (stagger != null) {
    return (
      <MotionTag
        className={className}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount }}
        variants={staggerContainer(stagger, delay)}
      >
        {children}
      </MotionTag>
    );
  }

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={VARIANT_MAP[variant]}
      transition={{ delay }}
    >
      {children}
    </MotionTag>
  );
}
